import {useCallback, useEffect, useMemo, useState} from 'react';

const DATE_VIEWS = ['scheduled'];
const SPECIAL_VIEWS = ['all', 'starred', ...DATE_VIEWS];

export {SPECIAL_VIEWS};

export default function useChecklistViews({checklistData, groups, onViewLabelChange}) {
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [focusedId, setFocusedId] = useState(null);
    const [innerView, setInnerView] = useState('checklists');
    const [allItemsMode, setAllItemsMode] = useState(false);
    const [starredItemsMode, setStarredItemsMode] = useState(false);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('checklistViewMode') || 'columns');

    const toggleViewMode = useCallback(mode => {
        setViewMode(mode);
        localStorage.setItem('checklistViewMode', mode);
    }, []);

    useEffect(() => {
        if (!focusedId) return;
        const handleKeyDown = e => {
            if (e.key === 'Escape') setFocusedId(null);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [focusedId]);

    const focusedChecklist = useMemo(
        () => (focusedId ? checklistData.find(c => c.id === focusedId) : null),
        [focusedId, checklistData],
    );

    const focusChecklist = useCallback(id => setFocusedId(id), []);
    const unfocusChecklist = useCallback(() => setFocusedId(null), []);

    const isReadOnlyView = selectedGroupId === 'starred' || DATE_VIEWS.includes(selectedGroupId);
    const isReorderable = !isReadOnlyView && selectedGroupId !== 'all';

    const visibleChecklists = useMemo(() => {
        if (selectedGroupId === 'all') return checklistData;
        if (selectedGroupId === 'starred') {
            return checklistData
                .filter(c => c.items.some(i => i.starred))
                .map(c => ({...c, items: c.items.filter(i => i.starred)}));
        }
        if (selectedGroupId === 'scheduled') return checklistData;
        if (selectedGroupId === null) return checklistData.filter(c => c.groupId == null);
        return checklistData.filter(c => c.groupId === selectedGroupId);
    }, [checklistData, selectedGroupId]);

    const allGroupedItems = useMemo(() => {
        if (selectedGroupId !== 'all' || !allItemsMode) return [];
        const groupMap = {};
        for (const g of groups) groupMap[g.id] = g.name;
        const buckets = {};
        for (const c of checklistData) {
            if (c.items.length === 0) continue;
            const groupName = c.groupId ? groupMap[c.groupId] || 'Default' : 'Default';
            if (!buckets[groupName]) buckets[groupName] = [];
            buckets[groupName].push({checklistId: c.id, checklistTitle: c.title, items: c.items});
        }
        return Object.entries(buckets).map(([groupName, checklists]) => ({groupName, checklists}));
    }, [checklistData, groups, selectedGroupId, allItemsMode]);

    const starredGroupedItems = useMemo(() => {
        if (selectedGroupId !== 'starred' || !starredItemsMode) return [];
        const groupMap = {};
        for (const g of groups) groupMap[g.id] = g.name;
        const buckets = {};
        for (const c of checklistData) {
            const starredItems = c.items.filter(i => i.starred);
            if (starredItems.length === 0) continue;
            const groupName = c.groupId ? groupMap[c.groupId] || 'Default' : 'Default';
            if (!buckets[groupName]) buckets[groupName] = [];
            buckets[groupName].push({checklistId: c.id, checklistTitle: c.title, items: starredItems});
        }
        return Object.entries(buckets).map(([groupName, checklists]) => ({groupName, checklists}));
    }, [checklistData, groups, selectedGroupId, starredItemsMode]);

    const selectGroup = useCallback(id => {
        setInnerView('checklists');
        setSelectedGroupId(id);
    }, []);
    const showTemplates = useCallback(() => {
        setInnerView('templates');
        setSelectedGroupId(undefined);
    }, []);

    const handleDeletedGroup = useCallback(id => {
        setSelectedGroupId(prev => (prev === id ? 'all' : prev));
    }, []);

    const handleChecklistCreated = useCallback(created => {
        setFocusedId(created.id);
    }, []);

    const handleSpawn = useCallback(checklist => {
        setInnerView('checklists');
        setSelectedGroupId(checklist.groupId || null);
    }, []);

    const handleDeletedChecklist = useCallback(id => {
        setFocusedId(prev => (prev === id ? null : prev));
    }, []);

    useEffect(() => {
        if (innerView === 'templates') {
            onViewLabelChange('Templates');
            return;
        }
        if (selectedGroupId === 'all') {
            onViewLabelChange('All');
            return;
        }
        if (selectedGroupId === 'starred') {
            onViewLabelChange('Starred');
            return;
        }
        if (selectedGroupId === 'scheduled') {
            onViewLabelChange('Scheduled');
            return;
        }
        if (selectedGroupId === null) {
            onViewLabelChange(groups.length > 0 ? 'Default' : 'All');
            return;
        }
        const group = groups.find(g => g.id === selectedGroupId);
        onViewLabelChange(group ? group.name : 'All');
    }, [selectedGroupId, groups, innerView, onViewLabelChange]);

    return {
        selectedGroupId,
        focusedId,
        focusedChecklist,
        innerView,
        allItemsMode,
        setAllItemsMode,
        starredItemsMode,
        setStarredItemsMode,
        viewMode,
        toggleViewMode,
        visibleChecklists,
        allGroupedItems,
        starredGroupedItems,
        isReadOnlyView,
        isReorderable,
        focusChecklist,
        unfocusChecklist,
        selectGroup,
        showTemplates,
        handleDeletedGroup,
        handleChecklistCreated,
        handleSpawn,
        handleDeletedChecklist,
    };
}
